import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import { api, USER_ID } from "../api";
import { SectionTitle, DishCard, rewardToast } from "../components";
import { ModuleGate } from "../flags";

const REGION_IMGS = {
  amritsar: "https://images.pexels.com/photos/28075291/pexels-photo-28075291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ludhiana: "https://images.pexels.com/photos/12089284/pexels-photo-12089284.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  patiala: "https://images.pexels.com/photos/20408437/pexels-photo-20408437.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  majha: "https://images.pexels.com/photos/29173093/pexels-photo-29173093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  malwa: "https://images.pexels.com/photos/28674557/pexels-photo-28674557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  doaba: "https://images.pexels.com/photos/12089284/pexels-photo-12089284.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function Punjab() {
  return (
    <ModuleGate flag="explore_punjab">
      <PunjabInner />
    </ModuleGate>
  );
}

function PunjabInner() {
  const [regions, setRegions] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data));
  }, []);

  const openRegion = async (slug) => {
    const r = await api.get(`/regions/${slug}`);
    setOpen(r.data);
    const res = await api.post(`/regions/${slug}/explore`, { user_id: USER_ID });
    rewardToast(res.data);
  };

  return (
    <div data-testid="punjab-page" className="px-4 pt-6 pb-10 mx-auto w-full max-w-3xl">
      <SectionTitle kicker="Explore Punjab" title="A culinary journey" testId="punjab-title" />
      <p className="text-sm text-white/60 mb-5">Punjab is bigger than butter chicken. Discover the food, stories and streets behind the menu.</p>
      <div className="grid grid-cols-2 gap-3">
        {regions.map((region, i) => (
          <button
            key={region.slug}
            data-testid={`region-card-${region.slug}`}
            onClick={() => openRegion(region.slug)}
            className={`relative rounded-2xl overflow-hidden border border-white/10 text-left ${i % 3 === 0 ? "col-span-2 h-40" : "h-36"}`}
          >
            <img src={REGION_IMGS[region.slug]} alt={region.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/95 via-[#050505]/40 to-transparent" />
            <div className="absolute bottom-0 p-4">
              <h3 className="font-display text-2xl font-bold tracking-tight">{region.name}</h3>
              <p className="text-[11px] text-[#D4AF37]">{region.tagline}</p>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 flex justify-center items-end" onClick={() => setOpen(null)}>
            <motion.div initial={{ y: 200 }} animate={{ y: 0 }} exit={{ y: 300 }} transition={{ type: "tween", duration: 0.25 }}
              data-testid="region-sheet" onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md md:max-w-lg bg-[#121212] rounded-t-3xl border-t border-[#D4AF37]/30 max-h-[80vh] overflow-y-auto p-5 pb-24">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2 text-[#D4AF37]"><MapPin size={14} /><span className="text-[10px] tracking-[0.3em]">DESTINATION</span></div>
                  <h2 className="font-display text-3xl font-bold tracking-tight">{open.name}</h2>
                </div>
                <button data-testid="region-close" onClick={() => setOpen(null)} aria-label="Close"><X size={20} className="text-white/60" /></button>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">{open.story}</p>
              <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-3 mt-4">
                <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] mb-1">TRIVIA</p>
                <p className="text-xs text-white/70">{open.trivia}</p>
              </div>
              <p className="text-[9px] text-white/25 mt-2">{open.review_status}</p>
              <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] mt-5 mb-3">TASTE IT ON THE URBAN MENU</p>
              <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar">
                {open.dish_details.map((d) => <DishCard key={d.slug} dish={d} compact />)}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
